import { AlertCircle, CheckCircle2, DatabaseZap, RadioTower } from 'lucide-react';
import type { PortfolioOverview } from '../App';

type DataHealthProps = {
  overview: PortfolioOverview;
  fetchedAt: string;
};

type HealthStatus = 'ok' | 'warn' | 'missing';

export function DataHealth({ overview, fetchedAt }: DataHealthProps) {
  const quoteAges = overview.holdings
    .map((holding) => (holding.quoteTime ? secondsBetween(holding.quoteTime, fetchedAt) : null))
    .filter((age): age is number => age != null && Number.isFinite(age));
  const freshestQuote = quoteAges.length > 0 ? Math.min(...quoteAges) : null;
  const staleQuotes = overview.holdings.filter((holding) => {
    if (!holding.quoteTime || holding.staleAfterSeconds == null) return false;
    return secondsBetween(holding.quoteTime, fetchedAt) > holding.staleAfterSeconds;
  }).length;
  const brokerBacked = overview.holdings.filter((holding) => typeof holding.brokerMarketValue === 'number').length;
  const missingValues = overview.holdings.filter(
    (holding) => holding.latestQuotePrice == null && holding.brokerMarketValue == null
  ).length;

  const items = [
    {
      label: 'Market API',
      detail:
        freshestQuote == null
          ? 'No quote timestamp in overview'
          : `${formatAge(freshestQuote)} old${staleQuotes ? `; ${staleQuotes} stale quote${staleQuotes === 1 ? '' : 's'}` : ''}`,
      status: freshestQuote == null ? 'missing' : staleQuotes ? 'warn' : 'ok',
      icon: <RadioTower size={16} aria-hidden="true" />
    },
    {
      label: 'IBKR push',
      detail:
        brokerBacked === 0
          ? 'No broker market values in overview'
          : `${brokerBacked}/${overview.holdings.length} holdings include broker value`,
      status: brokerBacked === 0 ? 'missing' : brokerBacked === overview.holdings.length ? 'ok' : 'warn',
      icon: <DatabaseZap size={16} aria-hidden="true" />
    },
    {
      label: 'D1 overview',
      detail: missingValues ? `${missingValues} holdings missing value` : `${overview.holdings.length} holdings read`,
      status: missingValues ? 'warn' : 'ok',
      icon: <DatabaseZap size={16} aria-hidden="true" />
    }
  ] satisfies Array<{ label: string; detail: string; status: HealthStatus; icon: JSX.Element }>;

  return (
    <section className="dashboard-panel data-health-panel" aria-labelledby="data-health-heading">
      <div className="panel-heading">
        <div>
          <h2 id="data-health-heading">Data health</h2>
          <p>Source status is inferred from the current overview payload.</p>
        </div>
      </div>

      <div className="health-list">
        {items.map((item) => (
          <div className={`health-row ${item.status}`} key={item.label}>
            <div className="health-icon">{item.status === 'ok' ? <CheckCircle2 size={16} aria-hidden="true" /> : <AlertCircle size={16} aria-hidden="true" />}</div>
            <div>
              <strong>{item.label}</strong>
              <span>{item.detail}</span>
            </div>
            <div className="health-source">{item.icon}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function secondsBetween(startIso: string, endIso: string): number {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((end - start) / 1000));
}

function formatAge(seconds: number): string {
  if (!Number.isFinite(seconds)) return 'unknown age';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}
